import React from "react";

import { AssistantToolUseContent } from "@/Chat/ChatSidebar/ChatByIdView/ChatMessage/AssistantToolUseContent.tsx";
import {
  ChatMessage,
  ChatRole,
  isChatTextContent,
  isChatToolUseContent,
  isChatToolResultContent,
  isChatErrorContent,
  ChatMessageStatus,
} from "@/shared/domain/chat/ChatRecord";

import { AssistantErrorContent } from "./AssistantErrorContent";
import { AssistantTextContent } from "./AssistantTextContent";
import { AssistantToolResultContent } from "./AssistantToolResultContent";
import styles from "./ChatMessageComponents.module.css";
import { UserMessage } from "./UserMessage";

interface ChatMessageComponentsProps {
  message: ChatMessage;
  isLastMessageInChat: boolean;
  onLinkClick?: () => void;
  onRetry?: () => void;
  autoNavDedupeKey: string;
  autoNavEnabled: boolean;
  isCancelling?: boolean;
}

const ChatMessageComponents = ({
  message,
  onLinkClick,
  onRetry,
  isLastMessageInChat,
  autoNavDedupeKey,
  autoNavEnabled,
  isCancelling = false,
}: ChatMessageComponentsProps) => {
  const components: React.ReactNode[] = [];
  let msgIdx = 0;

  if (message.role === ChatRole.USER) {
    message.content.forEach((content) => {
      if (isChatTextContent(content)) {
        const isFailed = message.status === ChatMessageStatus.FAILED;

        components.push(
          <UserMessage
            key={msgIdx++}
            text={content.text}
            isFailed={isFailed}
            onRetry={isFailed ? onRetry : undefined}
          />,
        );
      }
    });
  }

  if (message.role === ChatRole.ASSISTANT) {
    message.content.forEach((content, idx) => {
      const isLastContentInMessage = idx === message.content.length - 1;
      const isLastContentInChat = isLastContentInMessage && isLastMessageInChat;

      if (isChatTextContent(content)) {
        components.push(
          <AssistantTextContent
            key={msgIdx++}
            text={content.text}
            isGenerating={
              isLastContentInChat &&
              message.status === ChatMessageStatus.GENERATING
            }
            isCancelling={isLastContentInChat && isCancelling}
            onLinkClick={onLinkClick}
            autoNavDedupeKey={`${autoNavDedupeKey}-${idx}`}
            autoNavEnabled={autoNavEnabled}
          />,
        );
      }

      if (isChatToolUseContent(content)) {
        if (isLastMessageInChat) {
          components.push(
            <AssistantToolUseContent key={msgIdx++} toolUse={content} />,
          );
        }
      }

      if (isChatErrorContent(content)) {
        components.push(
          <AssistantErrorContent key={msgIdx++} error={content} />,
        );
      }
    });
  }

  if (message.role === ChatRole.ROBOTO) {
    message.content.forEach((content) => {
      if (isChatToolResultContent(content)) {
        components.push(
          <AssistantToolResultContent key={msgIdx++} toolResult={content} />,
        );
      }

      if (isChatErrorContent(content)) {
        components.push(
          <AssistantErrorContent key={msgIdx++} error={content} />,
        );
      }
    });
  }

  if (components.length === 0) {
    return null;
  }

  return <div className={styles.mainContainer}>{components}</div>;
};

export { ChatMessageComponents };
